import {useEffect, useRef, useState} from 'react';
import {
    Autocomplete,
    Button,
    Collapse,
    Drawer,
    Group,
    Radio,
    ScrollArea,
    Stack,
    Stepper,
    Text,
} from '@mantine/core';
import {Field, FieldArray, Formik, useFormikContext} from 'formik';
import {
    FormikRadioGroup,
    FormikSelect,
    FormikTextarea,
    FormikTextInput,
    PlatformFieldset,
    FormWithButtons,
} from '@flexinet/ui-components';
import {PipelineTemplatesAutocomplete} from '../../pipeline-templates/inputs/PipelineTemplatesAutocomplete';
import {DeploymentAccountGroupsAutocomplete} from '../../deployment-account-groups/inputs/DeploymentAccountGroupsAutocomplete';
import {useDeploymentAccountGroups} from '../../deployment-account-groups/hooks/useDeploymentAccountGroups';
import {DeploymentAccountGroup} from '../../deployment-account-groups/types';
import {useCodeRepositories} from '../../code-repositories/hooks/useCodeRepositories';
import {useAnalyzeRepositoryQuery, useProvisionPipelineMutation} from '../hooks/usePipelinesQuery';
import {DeploymentAccount, PipelineStage, ProvisionPipelineInput, RepositoryAnalysis} from '../types';
import {CodeRepository} from '../../code-repositories/types';
import {AnalysisResultPanel} from '../components/AnalysisResultPanel';

// FNP-450: canonical platform order for deployment accounts — TEST before PROD
// before anything else. Mirror of the backend sort in
// archetype-defaults.ts:sortDeploymentAccountsByEnvironment. Sorting here as
// defense in depth so the displayed list matches the pipeline that gets
// provisioned even if the backend ever loses the sort.
const sortByEnvironment = (accounts: DeploymentAccount[]): DeploymentAccount[] => {
    const rank = (acct: DeploymentAccount): number => {
        const name = (acct.accountName || '').toLowerCase();
        if (name.endsWith('-test') || name.includes('test')) return 0;
        if (name.endsWith('-prod') || name.includes('prod')) return 1;
        return 2;
    };
    return [...accounts].sort((a, b) => rank(a) - rank(b));
};

// FNP-444: when the user picks a different deployment account group on the
// provision form, re-derive `deploymentAccounts` to that group's TEST + PROD
// members. Without this listener, the analyzer-suggested array stays put and
// the user's deliberate OU choice is silently dropped at submit. Preserves any
// `deploymentBucketName` overrides keyed by accountId across the swap so a
// user who edited a bucket name doesn't lose it just by changing the group.
const DeploymentAccountGroupSync = ({
                                        groups,
                                    }: { groups: DeploymentAccountGroup[] }) => {
    const {values, setFieldValue} = useFormikContext<ProvisionPipelineInput>();
    const lastGroupIdRef = useRef<string | undefined>(values.deploymentAccountGroupId);

    useEffect(() => {
        const groupId = values.deploymentAccountGroupId;
        if (!groupId || groupId === lastGroupIdRef.current) {
            lastGroupIdRef.current = groupId;
            return;
        }
        const group = groups.find((g) => g.id === groupId);
        if (!group) {
            lastGroupIdRef.current = groupId;
            return;
        }
        const previousBucketByAccountId: Record<string, string | undefined> = {};
        (values.deploymentAccounts ?? []).forEach((acct: DeploymentAccount) => {
            if (acct?.accountId && acct.deploymentBucketName) {
                previousBucketByAccountId[acct.accountId] = acct.deploymentBucketName;
            }
        });
        const next: DeploymentAccount[] = sortByEnvironment(
            group.accounts
                .filter((a) => a.environment === 'TEST' || a.environment === 'PROD')
                .map((a) => ({
                    accountId: a.accountId,
                    accountName: a.accountName,
                    region: a.region,
                    // FNP-694: preserve a user-edited bucket for the same account if one
                    // exists; otherwise DERIVE it from the same convention the backend
                    // analyzer uses (${repositoryName}-infra-${accountId}-ui). Without this,
                    // switching to a different OU (whose accounts have new accountIds) left
                    // every bucket field empty because previousBucketByAccountId never matched.
                    deploymentBucketName: previousBucketByAccountId[a.accountId]
                        ?? (values.repositoryName
                            ? `${values.repositoryName}-infra-${a.accountId}-ui`
                            : undefined),
                })),
        );
        setFieldValue('deploymentAccounts', next);
        lastGroupIdRef.current = groupId;
    }, [values.deploymentAccountGroupId, groups, setFieldValue, values.deploymentAccounts, values.repositoryName]);

    return null;
};

interface ProvisionPipelineDrawerProps {
    opened: boolean;
    onClose: () => void;
}

const hasS3Stage = (stages?: PipelineStage[]): boolean =>
    Array.isArray(stages) && stages.some((s) => s?.deployProvider === 'S3');

// FNP-693: a stage is a CloudFormation DEPLOY target (region is meaningful) when its
// stageKind is DEPLOY or it has a non-S3 deployProvider. S3 deploys don't use the
// per-stage region (they sync to a bucket), so they're excluded.
const isDeployStage = (stage?: PipelineStage): boolean =>
    !!stage && (stage.stageKind === 'DEPLOY' || (!!stage.deployProvider && stage.deployProvider !== 'S3'));

const hasDeployStage = (stages?: PipelineStage[]): boolean =>
    Array.isArray(stages) && stages.some(isDeployStage);

// FNP-693: per-stage deploy region options. us-west-2 is the default (preselected) and
// equals the pipeline region — the provisioner treats it as same-region (no cross-region
// action). us-east-1 is required for CloudFront ACM certificate stacks.
const DEFAULT_STAGE_REGION = 'us-west-2';
const STAGE_REGION_OPTIONS = [
    {value: 'us-west-2', label: 'us-west-2 (default)'},
    {value: 'us-east-1', label: 'us-east-1 (CloudFront certs)'},
];

export const ProvisionPipelineDrawer = ({opened, onClose}: ProvisionPipelineDrawerProps) => {

    const [active, setActive] = useState(0);
    const [repositoryName, setRepositoryName] = useState('');
    const [branchName, setBranchName] = useState('master');
    const [analysis, setAnalysis] = useState<RepositoryAnalysis | undefined>(undefined);
    const [showMoreDetails, setShowMoreDetails] = useState(false);

    const {items: repositories} = useCodeRepositories();
    const repoData = repositories.map((r: CodeRepository) => r.name);

    const {items: deploymentAccountGroups} = useDeploymentAccountGroups();

    const {refetch, isFetching: analyzing} = useAnalyzeRepositoryQuery(repositoryName, branchName || undefined);
    const provisionMutation = useProvisionPipelineMutation();

    const handleAnalyze = async () => {
        const result = await refetch();
        if (result.data) {
            setAnalysis(result.data);
            setActive(1);
        }
    };

    const handleConfirm = () => {
        setActive(2);
    };

    const handleProvision = (values: ProvisionPipelineInput) => {
        provisionMutation.mutate(values, {
            onSuccess: () => {
                onClose();
            },
        });
    };

    const initialValues: ProvisionPipelineInput = {
        repositoryName: analysis?.repositoryName || repositoryName,
        branchName: analysis?.branchName || branchName || undefined,
        name: analysis?.suggestedPipelineName || '',
        pipelineType: analysis?.suggestedPipelineType || undefined,
        archetype: analysis?.detectedArchetype || undefined,
        templateId: analysis?.suggestedTemplateId || undefined,
        buildCommand: analysis?.suggestedBuildCommand || undefined,
        // FNP-693: preselect us-west-2 (the default region) on each DEPLOY stage so the
        // Stage Regions selects render a value rather than blank. Non-deploy stages are
        // left untouched. us-west-2 == the pipeline region, so this changes nothing about
        // what the provisioner emits (no cross-region action).
        stages: (analysis?.suggestedStages ?? []).map((stage) =>
            isDeployStage(stage) ? {...stage, region: stage.region ?? DEFAULT_STAGE_REGION} : stage
        ),
        deploymentAccountGroupId: undefined,
        deploymentAccounts: analysis?.suggestedDeploymentAccounts ?? [],
        description: '',
    };

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-up',
                duration: 750,
                timingFunction: 'ease',
            }}
            position="right"
            offset={10}
            opened={opened}
            onClose={onClose}
            title="Provision Pipeline"
            padding="xl"
            size="xl"
            scrollAreaComponent={ScrollArea.Autosize}
        >
            <Stepper active={active} onStepClick={setActive} allowNextStepsSelect={false}>
                <Stepper.Step label="Select Repository" description="Choose a repo to analyze">
                    <Stack gap="xl" mt="xl">
                        <Autocomplete
                            label="Code Repository"
                            description="Select the repository to analyze"
                            data={repoData}
                            value={repositoryName}
                            onChange={setRepositoryName}
                            placeholder="Select repository"
                            withAsterisk
                        />
                        <Radio.Group
                            label="Branch Name"
                            description="Select the branch to analyze"
                            value={branchName}
                            onChange={(value) => setBranchName(value)}
                        >
                            <Group mt="xs">
                                <Radio value="master" label="master"/>
                                <Radio value="main" label="main"/>
                            </Group>
                        </Radio.Group>
                        <Group justify="flex-end">
                            <Button
                                onClick={handleAnalyze}
                                loading={analyzing}
                                disabled={!repositoryName}
                            >
                                Analyze
                            </Button>
                        </Group>
                    </Stack>
                </Stepper.Step>

                <Stepper.Step label="Review Analysis" description="Review suggestions">
                    {analysis && (
                        <Stack gap="xl" mt="xl">
                            <AnalysisResultPanel analysis={analysis}/>

                            <Group justify="flex-end">
                                <Button variant="default" onClick={() => setActive(0)}>
                                    Back
                                </Button>
                                <Button onClick={handleConfirm}>
                                    {analysis.confidence === 'HIGH' ? 'Confirm' : 'Edit'}
                                </Button>
                            </Group>
                        </Stack>
                    )}
                </Stepper.Step>

                <Stepper.Step label="Provision" description="Edit & provision">
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize
                        onSubmit={handleProvision}
                    >
                        {({handleSubmit, isSubmitting, ...rest}) => (
                            <FormWithButtons
                                handleSubmit={handleSubmit}
                                isSubmitting={isSubmitting || provisionMutation.isPending}
                                handleCancel={onClose}
                                debug={false}
                                {...rest}
                            >
                                <DeploymentAccountGroupSync groups={deploymentAccountGroups}/>
                                <Stack gap="xl">
                                    <PlatformFieldset legend="Pipeline Details">
                                        <Field
                                            name="name"
                                            label="Name"
                                            description="Pipeline name"
                                            component={FormikTextInput}
                                            withAsterisk
                                            my="xl"
                                        />
                                        <Field
                                            name="pipelineType"
                                            label="Pipeline Type"
                                            description="Select the pipeline type"
                                            component={FormikSelect}
                                            data={[
                                                {value: 'APPLICATION', label: 'Application'},
                                                {value: 'API', label: 'API'},
                                                {value: 'DATABASE', label: 'Database'},
                                                {value: 'INFRASTRUCTURE', label: 'Infrastructure'},
                                                {value: 'LIBRARY', label: 'Library'},
                                                {value: 'SERVICE', label: 'Service'},
                                            ]}
                                            withAsterisk
                                            my="xl"
                                        />
                                    </PlatformFieldset>

                                    <PlatformFieldset legend="Deployment">
                                        <Field
                                            name="deploymentAccountGroupId"
                                            label="Account Group"
                                            description="Select the deployment account group (optional)"
                                            component={DeploymentAccountGroupsAutocomplete}
                                            my="xl"
                                        />

                                        {hasS3Stage(rest.values.stages) && (
                                            <FieldArray name="deploymentAccounts">
                                                {() => (
                                                    <Stack gap="md" my="xl">
                                                        <Text fw={600}>Deployment Buckets (S3 stages only)</Text>
                                                        {(rest.values.deploymentAccounts ?? []).length === 0 && (
                                                            <Text size="sm" c="dimmed">
                                                                No deployment accounts suggested. Select an account group above or
                                                                re-analyze the repository.
                                                            </Text>
                                                        )}
                                                        {(rest.values.deploymentAccounts ?? []).map((account, index) => (
                                                            <PlatformFieldset
                                                                key={`${account.accountId}-${index}`}
                                                                legend={account.accountName
                                                                    ? `${account.accountName} (${account.accountId})`
                                                                    : account.accountId}
                                                            >
                                                                {account.region && (
                                                                    <Text size="sm" c="dimmed" my="xs">
                                                                        Region: {account.region}
                                                                    </Text>
                                                                )}
                                                                <Field
                                                                    name={`deploymentAccounts.${index}.deploymentBucketName`}
                                                                    label="Deployment Bucket (S3 stages only)"
                                                                    description="e.g. flexi-bot-app-infra-951377762314-ui — pre-populated from convention, edit if needed"
                                                                    component={FormikTextInput}
                                                                    my="xl"
                                                                />
                                                            </PlatformFieldset>
                                                        ))}
                                                    </Stack>
                                                )}
                                            </FieldArray>
                                        )}
                                    </PlatformFieldset>

                                    <Group justify="flex-start">
                                        <Button
                                            variant="subtle"
                                            onClick={() => setShowMoreDetails((v) => !v)}
                                        >
                                            {showMoreDetails ? 'Hide additional details' : 'More details'}
                                        </Button>
                                    </Group>

                                    <Collapse in={showMoreDetails}>
                                        <Stack gap="xl">
                                            <PlatformFieldset legend="Additional Details">
                                                <Field
                                                    name="description"
                                                    label="Description"
                                                    description="Pipeline description"
                                                    component={FormikTextarea}
                                                    my="xl"
                                                />
                                                <Field
                                                    name="templateId"
                                                    label="Template"
                                                    description="Select the pipeline template (optional)"
                                                    component={PipelineTemplatesAutocomplete}
                                                    my="xl"
                                                />
                                                <Field
                                                    name="buildCommand"
                                                    label="Build Command"
                                                    description="Build command (optional)"
                                                    component={FormikTextInput}
                                                    my="xl"
                                                />
                                            </PlatformFieldset>

                                            <PlatformFieldset legend="Repository">
                                                <Field
                                                    name="repositoryName"
                                                    label="Code Repository"
                                                    description="Repository name"
                                                    component={FormikTextInput}
                                                    disabled
                                                    my="xl"
                                                />
                                                <Field
                                                    name="branchName"
                                                    label="Branch Name"
                                                    description="Branch name"
                                                    component={FormikRadioGroup}
                                                    data={[
                                                        {label: 'master', value: 'master'},
                                                        {label: 'main', value: 'main'},
                                                    ]}
                                                    my="xl"
                                                />
                                            </PlatformFieldset>

                                            {/* FNP-693: per-stage deploy region. Hidden under "More
                                                details" — changed only rarely (a DEPLOY stage can target
                                                us-east-1, e.g. a CloudFront ACM cert, while siblings stay
                                                us-west-2). Only rendered when the pipeline has DEPLOY stages. */}
                                            {hasDeployStage(rest.values.stages) && (
                                                <PlatformFieldset legend="Stage Regions">
                                                    <FieldArray name="stages">
                                                        {() => (
                                                            <Stack gap="md" my="xl">
                                                                <Text size="sm" c="dimmed">
                                                                    Deploy region per stage. Leave as the default
                                                                    (us-west-2) unless a stage deploys a us-east-1
                                                                    resource such as a CloudFront certificate.
                                                                </Text>
                                                                {(rest.values.stages ?? []).map((stage, index) =>
                                                                    isDeployStage(stage) ? (
                                                                        <Field
                                                                            key={`${stage.name}-${index}`}
                                                                            name={`stages.${index}.region`}
                                                                            label={`${stage.name} region`}
                                                                            description="Target region for this deploy stage"
                                                                            component={FormikSelect}
                                                                            data={STAGE_REGION_OPTIONS}
                                                                        />
                                                                    ) : null
                                                                )}
                                                            </Stack>
                                                        )}
                                                    </FieldArray>
                                                </PlatformFieldset>
                                            )}
                                        </Stack>
                                    </Collapse>
                                </Stack>
                            </FormWithButtons>
                        )}
                    </Formik>
                </Stepper.Step>
            </Stepper>
        </Drawer>
    );
};
